<template>
  <div data-component="app-launcher-warning">
    <div
      v-if="emoji"
      class="emoji-image"
    >
      {{ emoji }}
    </div>

    <div class="title">
      {{ title }}
    </div>

    <div
      v-if="description"
      class="description"
    >
      {{ description }}
    </div>

    <app-launcher-warning-actions
      :app-state="appState"
      :app-error="appError"
      :v-suggest="vSuggest"
      :is-updating="isUpdating"
      :is-loading="isLoading"
      @app-install="$emit('app-install')"
      @app-update="$emit('app-update')"
      @accept-version="$emit('accept-version')"
      @retry-update="$emit('retry-update')"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue-demi';
import { TranslateResult } from 'vue-i18n';

import AppLauncherWarningActions from './AppLauncherWarningActions.vue';
import { APP_STATES, APP_STATE_PROP, APP_ERROR_PROP } from '../constants';

export default defineComponent({
  name: 'AppLauncherWarning',
  components: { AppLauncherWarningActions },
  props: {
    vSuggest: {
      type: String,
      default: '',
    },
    vTarget: {
      type: String,
      default: '',
    },
    appState: APP_STATE_PROP,
    appError: APP_ERROR_PROP,
    isUpdating: Boolean,
    isLoading: Boolean,
  },
  emits: ['app-install', 'app-update', 'accept-version', 'retry-update'],
  computed: {
    title() {
      const TITLES: Record<string, TranslateResult> = {
        [APP_STATES.NO_AGENT_ACCESS]: this.$t('HITL.app-launcher.no-agent-access-title'),
        [APP_STATES.NO_APP_AVAILABLE]: this.$t('HITL.app-launcher.no-app-available-title'),
        [APP_STATES.NO_APP_VERSION]: this.$t('HITL.app-launcher.no-app-version-title'),
        [APP_STATES.NO_APP_VERSION_SUPPORT]: this.$t('HITL.app-launcher.no-app-version-support-title'),
        [APP_STATES.NEEDS_DATA_UPDATE]: this.$t('HITL.app-launcher.app-needs-update-title'),
        [APP_STATES.DATA_UPDATE_ERROR_AGENT]: this.$t('HITL.app-launcher.update-error-title', { version: this.vTarget }),
        [APP_STATES.DATA_UPDATE_ERROR_ADMIN]: this.$t('HITL.app-launcher.update-error-title', { version: this.vTarget }),
        [APP_STATES.APP_ERROR]: this.$t('HITL.app-launcher.app-error-title'),
      };

      return TITLES[this.appState];
    },

    description() {
      const DESCRIPTIONS: Record<string, TranslateResult> = {
        [APP_STATES.NO_AGENT_ACCESS]: this.$t('HITL.app-launcher.no-agent-access-description'),
        [APP_STATES.NO_APP_AVAILABLE]: this.$t('HITL.app-launcher.no-app-available-description'),
        [APP_STATES.NO_APP_VERSION]: this.$t('HITL.app-launcher.no-app-version-description'),
        [APP_STATES.NO_APP_VERSION_SUPPORT]: this.$t('HITL.app-launcher.no-app-version-support-description'),
        [APP_STATES.NEEDS_DATA_UPDATE]: this.$t('HITL.app-launcher.app-needs-update-description'),
        [APP_STATES.DATA_UPDATE_ERROR_ADMIN]: this.$t('HITL.app-launcher.update-error-description'),
        [APP_STATES.DATA_UPDATE_ERROR_AGENT]: this.$t('HITL.app-launcher.no-agent-access-description'),
        [APP_STATES.APP_ERROR]: this.$t('HITL.app-launcher.app-error-description'),
      };

      return DESCRIPTIONS[this.appState];
    },

    emoji() {
      const NO_APP_EMOJI = '⛈️';
      const ERROR_EMOJI = '🥷';
      const WARNING_EMOJI = '🚧';

      switch (this.appState) {
        case APP_STATES.NO_AGENT_ACCESS:
        case APP_STATES.NO_APP_AVAILABLE:
          return NO_APP_EMOJI;
        case APP_STATES.NO_APP_VERSION:
        case APP_STATES.NO_APP_VERSION_SUPPORT:
        case APP_STATES.NEEDS_DATA_UPDATE:
        case APP_STATES.DATA_UPDATE_ERROR_AGENT:
        case APP_STATES.DATA_UPDATE_ERROR_ADMIN:
          return WARNING_EMOJI;
        case APP_STATES.APP_ERROR:
        default:
          return ERROR_EMOJI;
      }
    },
  },
});
</script>

<style lang="scss" scoped>
@use '@/styles' as *;

[data-component='app-launcher-warning'] {
  --emoji-image-size: 3.75rem;

  display: flex;
  flex-direction: column;
  align-items: center;

  @include md {
    width: 40rem;
  }

  .emoji-image {
    height: var(--emoji-image-size);
    width: var(--emoji-image-size);
    font-size: var(--emoji-image-size);
    line-height: 1;
  }

  .title,
  .description {
    text-align: center;
  }

  .title {
    margin-top: var(--s-4);
    font-size: 1.25rem;
    font-weight: var(--fw-2);
    color: var(--or-c-on-background);
  }

  .description {
    color: var(--or-c-outline);
    margin-top: var(--s-3);
  }

  [data-component='app-launcher-warning-actions'] {
    margin-top: 0.75rem;
  }
}
</style>
